import sanitizeHtml from 'sanitize-html';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import moment from 'moment';

import { __t, applyTranslations } from 'utils/language';
import { dataCommonToComponent } from 'utils/data/data';

import type { Channel, MixedSelectDropdownItem } from 'types';
import type { SOVReport, SOVReportType } from 'modules/insights/sov/types';

applyTranslations(require('../shared/sov-shared.i18n.json'));

const SHARED_MSG_KEY = 'sov_shared';

export const COLLABARY = 'collabary';
export const INSTAGRAM = 'instagram';
export const YOUTUBE = 'youtube';
export const REPORT = 'report';
export const QUERY = 'query';
export const ANALYSIS = 'analysis';
export const CREATE = 'create';
export const EDIT = 'edit';
export const DUPLICATE = 'duplicate';
export const DELETE = 'delete';
export const FIXED_TO_TOP = 'fixed-to-top';
export const REPORT_SAVED_TIMEOUT = 2000;
export const UNTITLED = __t(`${SHARED_MSG_KEY}.labels.untitled`);

export const MODAL_META_CHAR_LIMITS = {
  name: 65,
  description: 200
};

export const DATA_COLUMNS_INSTAGRAM = [
  'contentCreators',
  'followers',
  'posts',
  'impressions',
  'engagements',
  'engagementRate'
];

export const DATA_COLUMNS_YOUTUBE = [
  'contentCreators',
  'subscribers',
  'videosPosted',
  'views',
  'engagements',
  'engagementRate'
];

export const AUDIENCE_FILTER_OPTIONS = {
  country: 'country',
  gender: 'gender',
  age: 'age'
};

export const KPI_OPTIONS_INSTAGRAM = {
  contentCreators: 'content_creators',
  followers: 'followers',
  posts: 'posts',
  impressions: 'impressions',
  engagements: 'engagements',
  engagementRate: 'engagement_rate'
};

export const KPI_OPTIONS_YOUTUBE = {
  contentCreators: 'content_creators',
  subscribers: 'subscribers',
  posts: 'videos_posted',
  views: 'views',
  engagements: 'engagements',
  engagementRate: 'engagement_rate'
};

export const GRAPH_KPI_OPTIONS_INSTAGRAM = omit(KPI_OPTIONS_INSTAGRAM, ['engagementRate']);
export const GRAPH_KPI_OPTIONS_YOUTUBE = omit(KPI_OPTIONS_YOUTUBE, ['engagementRate']);
export const TOP_INFLUENCERS_KPI_OPTIONS_INSTAGRAM = omit(KPI_OPTIONS_INSTAGRAM, ['engagementRate', 'contentCreators']);
export const TOP_INFLUENCERS_KPI_OPTIONS_YOUTUBE = omit(KPI_OPTIONS_YOUTUBE, ['engagementRate', 'contentCreators']);

const allowedValues = /[^a-z\u00E0-\u00FC&0-9_.]/ig;

const SANITIZE_HTML_CONFIG = {
  allowedTags: ['strong']
};

export const sanitizeValue = (value: string): string => value.replace(allowedValues, '');

export const sanitizeMessage = (message: string): string => sanitizeHtml(message, SANITIZE_HTML_CONFIG);

export const getReplacementValue = (value: string): string => value.replace(/(\+)/, ' $1 ');

export const getRandomInt = (max: number): number => Math.floor(Math.random() * Math.floor(max));

export const mapItemsToCommon = (items: Array<MixedSelectDropdownItem>, selectedItems: Array<string | undefined> = []): Array<MixedSelectDropdownItem> => (items || []).map(item => (
  dataCommonToComponent({
    ...item,
    selected: selectedItems.includes(item.label),
    value: item.label || ''
  })
));

export const isReadyForAnalysis = (report: SOVReport): boolean | undefined | null => {
  if (isNil(report)) {
    return null;
  }

  const requirements = [
    {
      name: 'countries',
      test: !isEmpty(report?.countries)
    },
    {
      name: 'startDate',
      test: !isNil(report.startDate)
    },
    {
      name: 'endDate',
      test: !isNil(report.endDate)
    },
    {
      name: 'startDateTooEarly',
      test: moment(report.startDate).isSameOrAfter('2018-01-01')
    }
  ];

  const checks = requirements.filter(requirement => (
    requirement.test
  ));

  return checks.length === requirements.length;
};

export const getChannelValue = (influencerChannel: Channel, reportType: SOVReportType): string => {
  const isInstagram = reportType === 'instagram';
  const channelProp = isInstagram ? 'username' : 'title';

  if (influencerChannel[channelProp]) {
    return `${isInstagram ? '@' : ''}${String(influencerChannel[channelProp])}`;
  }

  return '';
};
